import { useState, useEffect, useCallback, useMemo } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import { House, MagnifyingGlass, MapPin, Ruler, Bed, CaretLeft, CaretRight, FunnelSimple } from '@phosphor-icons/react';

const API = process.env.REACT_APP_BACKEND_URL;

const PROPERTY_TYPE_OPTIONS = [
    { value: 'all', label: 'Alle Typen' },
    { value: 'apartment', label: 'Wohnung' },
    { value: 'house', label: 'Haus' },
    { value: 'land', label: 'Grundstück' },
    { value: 'office', label: 'Büro' },
    { value: 'retail', label: 'Einzelhandel' },
    { value: 'warehouse', label: 'Lager/Halle' },
    { value: 'investment', label: 'Renditeobjekt' },
    { value: 'international', label: 'Auslandsimmobilien' },
    { value: 'other', label: 'Sonstige' },
];

const MARKETING_TYPE_OPTIONS = [
    { value: 'all', label: 'Kaufen & Mieten' },
    { value: 'sale', label: 'Kaufen' },
    { value: 'rent', label: 'Mieten' },
];

const ENERGY_CLASS_OPTIONS = [
    { value: 'all', label: 'Alle Klassen' },
    { value: 'A+', label: 'A+' },
    { value: 'A', label: 'A' },
    { value: 'B', label: 'B' },
    { value: 'C', label: 'C' },
    { value: 'D', label: 'D' },
    { value: 'E', label: 'E' },
    { value: 'F', label: 'F' },
    { value: 'G', label: 'G' },
    { value: 'H', label: 'H' },
];

const PROPERTY_TYPE_LABELS = {
    apartment: 'Wohnung',
    house: 'Haus',
    land: 'Grundstück',
    office: 'Büro',
    retail: 'Einzelhandel',
    warehouse: 'Lager/Halle',
    investment: 'Renditeobjekt',
    international: 'Auslandsimmobilien',
    other: 'Sonstige'
};

export default function PublicSearchPage() {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const [properties, setProperties] = useState([]);
    const [loading, setLoading] = useState(true);
    const [total, setTotal] = useState(0);
    const [selectedProperty, setSelectedProperty] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);

    // Auto-resize for iframe embed
    useEffect(() => {
        const sendHeight = () => {
            const h = document.body.scrollHeight;
            window.parent.postMessage({ type: 'immo-embed-resize', height: h }, '*');
        };
        const observer = new ResizeObserver(sendHeight);
        observer.observe(document.body);
        return () => observer.disconnect();
    }, []);
    
    // Get config from URL params
    const showFilters = searchParams.get('filters') !== 'false';
    const theme = searchParams.get('theme') || 'light';
    const itemsPerPage = parseInt(searchParams.get('limit')) || 12;
    const searchFieldsParam = searchParams.get('search_fields');
    const activeSearchFields = useMemo(() => 
        searchFieldsParam ? searchFieldsParam.split(',') : ['city', 'object_id', 'title'],
        [searchFieldsParam]
    );
    const showSearch = activeSearchFields.length > 0;
    
    // Filters
    const [search, setSearch] = useState('');
    const [propertyType, setPropertyType] = useState(
        searchParams.get('types')?.split(',')[0] || 'all'
    );
    const [marketingType, setMarketingType] = useState(
        searchParams.get('marketing') || 'all'
    );
    const [yearBuiltMin, setYearBuiltMin] = useState('');
    const [yearBuiltMax, setYearBuiltMax] = useState('');
    const [floorMin, setFloorMin] = useState('');
    const [floorMax, setFloorMax] = useState('');
    const [energyClass, setEnergyClass] = useState('all');
    const [showAdvanced, setShowAdvanced] = useState(false);

    const totalPages = Math.ceil(total / itemsPerPage);

    const fetchProperties = useCallback(async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams();
            if (search) {
                params.append('search', search);
                params.append('search_fields', activeSearchFields.join(','));
            }
            if (propertyType !== 'all') params.append('property_type', propertyType);
            if (marketingType !== 'all') params.append('marketing_type', marketingType);
            if (yearBuiltMin) params.append('year_built_min', yearBuiltMin);
            if (yearBuiltMax) params.append('year_built_max', yearBuiltMax);
            if (floorMin) params.append('floor_min', floorMin);
            if (floorMax) params.append('floor_max', floorMax);
            if (energyClass !== 'all') params.append('energy_efficiency_class', energyClass);
            params.append('limit', itemsPerPage.toString());
            params.append('skip', ((currentPage - 1) * itemsPerPage).toString());
            
            const brokerId = searchParams.get('broker');
            if (brokerId) params.append('broker_id', brokerId);

            // Embed-Spezialfilter: Anlageimmobilien / Internationale Immobilien
            if (searchParams.get('investment') === 'true') params.append('investment', 'true');
            if (searchParams.get('international') === 'true') params.append('international', 'true');
            
            const { data } = await axios.get(`${API}/api/public/properties?${params.toString()}`);
            setProperties(data.properties);
            setTotal(data.total);
        } catch (error) {
            console.error('Error fetching properties:', error);
        } finally {
            setLoading(false);
        }
    }, [search, propertyType, marketingType, yearBuiltMin, yearBuiltMax, floorMin, floorMax, energyClass, itemsPerPage, currentPage, searchParams, activeSearchFields]);

    useEffect(() => {
        const debounce = setTimeout(() => {
            fetchProperties();
        }, 300);
        return () => clearTimeout(debounce);
    }, [fetchProperties]);

    // Reset page when filters change
    useEffect(() => {
        setCurrentPage(1);
    }, [search, propertyType, marketingType, yearBuiltMin, yearBuiltMax, floorMin, floorMax, energyClass]);

    const searchPlaceholder = (() => {
        const labels = [];
        if (activeSearchFields.includes('title')) labels.push('Titel');
        if (activeSearchFields.includes('city')) labels.push('Ort');
        if (activeSearchFields.includes('object_id')) labels.push('Objekt-Nr.');
        return labels.length > 0 ? `${labels.join(', ')} suchen...` : 'Suchen...';
    })();

    const formatPrice = (price, currency = 'EUR', marketingType = 'sale') => {
        if (!price) return marketingType === 'rent' ? 'Miete auf Anfrage' : 'Kaufpreis auf Anfrage';
        return new Intl.NumberFormat('de-DE', {
            style: 'currency',
            currency: currency,
            maximumFractionDigits: 0
        }).format(price);
    };

    const PropertyCard = ({ property }) => (
        <Card 
            className="border overflow-hidden card-hover cursor-pointer"
            onClick={() => navigate(`/expose/${property.id}`)}
            data-testid={`public-property-${property.id}`}
        >
            {/* Image */}
            <div className="aspect-video bg-muted relative">
                {property.images && property.images.length > 0 ? (
                    <img
                        src={`${API}/api/public/files/${property.images[0]}`}
                        alt={property.title}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                            e.target.src = 'https://images.unsplash.com/photo-1559329146-807aff9ff1fb?w=400';
                        }}
                    />
                ) : (
                    <div className="w-full h-full flex items-center justify-center">
                        <House size={48} className="text-muted-foreground opacity-50" />
                    </div>
                )}
                <Badge className="absolute top-2 left-2 rounded-sm">
                    {property.marketing_type === 'rent' ? 'Miete' : 'Kauf'}
                </Badge>
            </div>
            
            <CardContent className="p-4">
                <h3 className="font-semibold truncate mb-1" style={{ fontFamily: 'var(--font-cabinet)' }}>
                    {property.title}
                </h3>
                
                <p className="text-sm text-muted-foreground flex items-center gap-1 mb-3">
                    <MapPin size={14} />
                    {property.address?.city || 'Keine Adresse'}
                </p>
                
                <div className="flex items-center gap-4 text-sm text-muted-foreground mb-3">
                    {property.area && (
                        <span className="flex items-center gap-1">
                            <Ruler size={14} />
                            {property.area} m²
                        </span>
                    )}
                    {property.rooms && (
                        <span className="flex items-center gap-1">
                            <Bed size={14} />
                            {property.rooms} Zimmer
                        </span>
                    )}
                </div>
                
                <div className="flex items-center justify-between">
                    <p className="font-bold text-lg" style={{ fontFamily: 'var(--font-cabinet)' }}>
                        {formatPrice(property.price, property.currency, property.marketing_type)}
                        {property.marketing_type === 'rent' && !!property.price && <span className="text-sm font-normal">/Monat</span>}
                    </p>
                    <Badge variant="outline" className="rounded-sm">
                        {PROPERTY_TYPE_LABELS[property.property_type] || property.property_type}
                    </Badge>
                </div>
            </CardContent>
        </Card>
    );

    const PropertyDetail = ({ property, onClose }) => (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
            <Card className="max-w-2xl w-full max-h-[90vh] overflow-auto" onClick={(e) => e.stopPropagation()}>
                {/* Image */}
                <div className="aspect-video bg-muted relative">
                    {property.images && property.images.length > 0 ? (
                        <img
                            src={`${API}/api/public/files/${property.images[0]}`}
                            alt={property.title}
                            className="w-full h-full object-cover"
                            onError={(e) => {
                                e.target.src = 'https://images.unsplash.com/photo-1559329146-807aff9ff1fb?w=800';
                            }}
                        />
                    ) : (
                        <div className="w-full h-full flex items-center justify-center">
                            <House size={64} className="text-muted-foreground opacity-50" />
                        </div>
                    )}
                </div>
                
                <CardContent className="p-6 space-y-4">
                    <div className="flex items-start justify-between">
                        <div>
                            <Badge className="mb-2 rounded-sm">
                                {property.marketing_type === 'rent' ? 'Miete' : 'Kauf'}
                            </Badge>
                            <h2 className="text-2xl font-bold" style={{ fontFamily: 'var(--font-cabinet)' }}>
                                {property.title}
                            </h2>
                            <p className="text-muted-foreground flex items-center gap-1 mt-1">
                                <MapPin size={16} />
                                {[property.address?.zip, property.address?.city].filter(Boolean).join(' ') || 'Keine Adresse'}
                            </p>
                        </div>
                        <div className="text-right">
                            <p className="text-2xl font-bold" style={{ fontFamily: 'var(--font-cabinet)' }}>
                                {formatPrice(property.price, property.currency, property.marketing_type)}
                            </p>
                            {property.marketing_type === 'rent' && !!property.price && (
                                <p className="text-muted-foreground">/Monat</p>
                            )}
                        </div>
                    </div>
                    
                    <div className="grid grid-cols-3 gap-4 py-4 border-y">
                        <div className="text-center">
                            <p className="text-2xl font-bold">{property.area || '-'}</p>
                            <p className="text-sm text-muted-foreground">m²</p>
                        </div>
                        <div className="text-center">
                            <p className="text-2xl font-bold">{property.rooms || '-'}</p>
                            <p className="text-sm text-muted-foreground">Zimmer</p>
                        </div>
                        <div className="text-center">
                            <p className="text-2xl font-bold">{PROPERTY_TYPE_LABELS[property.property_type] || '-'}</p>
                            <p className="text-sm text-muted-foreground">Typ</p>
                        </div>
                    </div>
                    
                    {property.description && (
                        <div>
                            <h3 className="font-semibold mb-2">Beschreibung</h3>
                            <p className="text-muted-foreground whitespace-pre-wrap">
                                {property.description}
                            </p>
                        </div>
                    )}
                    
                    {property.features && property.features.length > 0 && (
                        <div>
                            <h3 className="font-semibold mb-2">Ausstattung</h3>
                            <div className="flex flex-wrap gap-2">
                                {property.features.map((feature, idx) => (
                                    <Badge key={idx} variant="outline" className="rounded-sm">
                                        {feature}
                                    </Badge>
                                ))}
                            </div>
                        </div>
                    )}
                    
                    <Button className="w-full rounded-sm" onClick={onClose}>
                        Schließen
                    </Button>
                </CardContent>
            </Card>
        </div>
    );

    return (
        <div className={`min-h-screen p-4 ${theme === 'dark' ? 'bg-gray-900 text-white' : 'bg-[#F8F9FA]'}`} data-testid="public-search-page">
            <div className="max-w-6xl mx-auto space-y-6">
                {/* Header */}
                <div className="text-center py-4">
                    <h1 className="text-3xl font-bold tracking-tight" style={{ fontFamily: 'var(--font-cabinet)' }}>
                        Immobilien
                    </h1>
                </div>

                {/* Filters */}
                {showFilters && (
                    <Card className="border" data-testid="public-filters">
                        <CardContent className="pt-6">
                            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                                {showSearch && (
                                    <div className="relative">
                                        <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                                        <Input
                                            placeholder={searchPlaceholder}
                                            value={search}
                                            onChange={(e) => setSearch(e.target.value)}
                                            className="pl-9 rounded-sm"
                                            data-testid="public-search-input"
                                        />
                                    </div>
                                )}
                                <Select value={propertyType} onValueChange={setPropertyType}>
                                    <SelectTrigger className="rounded-sm" data-testid="public-type-filter">
                                        <SelectValue placeholder="Immobilientyp" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {PROPERTY_TYPE_OPTIONS.map((opt) => (
                                            <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                                <Select value={marketingType} onValueChange={setMarketingType}>
                                    <SelectTrigger className="rounded-sm" data-testid="public-marketing-filter">
                                        <SelectValue placeholder="Vermarktung" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {MARKETING_TYPE_OPTIONS.map((opt) => (
                                            <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                                <Button 
                                    variant={showAdvanced ? "default" : "outline"} 
                                    className="rounded-sm"
                                    onClick={() => setShowAdvanced(!showAdvanced)}
                                    data-testid="public-toggle-advanced-filters"
                                >
                                    <FunnelSimple size={16} className="mr-2" />
                                    Erweiterte Filter
                                </Button>
                            </div>
                            
                            {/* Advanced Filters */}
                            {showAdvanced && (
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4 pt-4 border-t" data-testid="public-advanced-filters">
                                    <div>
                                        <label className="text-xs font-medium text-muted-foreground mb-1.5 block">Baujahr</label>
                                        <div className="flex gap-2">
                                            <Input
                                                type="number"
                                                placeholder="Von"
                                                value={yearBuiltMin}
                                                onChange={(e) => setYearBuiltMin(e.target.value)}
                                                className="rounded-sm"
                                                data-testid="public-year-built-min"
                                            />
                                            <Input
                                                type="number"
                                                placeholder="Bis"
                                                value={yearBuiltMax}
                                                onChange={(e) => setYearBuiltMax(e.target.value)}
                                                className="rounded-sm"
                                                data-testid="public-year-built-max"
                                            />
                                        </div>
                                    </div>
                                    <div>
                                        <label className="text-xs font-medium text-muted-foreground mb-1.5 block">Etage</label>
                                        <div className="flex gap-2">
                                            <Input
                                                type="number"
                                                placeholder="Von"
                                                value={floorMin}
                                                onChange={(e) => setFloorMin(e.target.value)}
                                                className="rounded-sm"
                                                data-testid="public-floor-min"
                                            />
                                            <Input
                                                type="number"
                                                placeholder="Bis"
                                                value={floorMax}
                                                onChange={(e) => setFloorMax(e.target.value)}
                                                className="rounded-sm"
                                                data-testid="public-floor-max"
                                            />
                                        </div>
                                    </div>
                                    <div>
                                        <label className="text-xs font-medium text-muted-foreground mb-1.5 block">Energieeffizienzklasse</label>
                                        <Select value={energyClass} onValueChange={setEnergyClass}>
                                            <SelectTrigger className="rounded-sm" data-testid="public-energy-class-filter">
                                                <SelectValue placeholder="Energieklasse" />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {ENERGY_CLASS_OPTIONS.map((opt) => (
                                                    <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                </div>
                            )}
                        </CardContent>
                    </Card>
                )}

                {/* Results */}
                {loading ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {[...Array(6)].map((_, i) => (
                            <Card key={i} className="border overflow-hidden">
                                <Skeleton className="aspect-video" />
                                <CardContent className="p-4 space-y-2">
                                    <Skeleton className="h-6 w-3/4" />
                                    <Skeleton className="h-4 w-1/2" />
                                    <Skeleton className="h-8 w-1/3" />
                                </CardContent>
                            </Card>
                        ))}
                    </div>
                ) : properties.length === 0 ? (
                    <Card className="border">
                        <CardContent className="py-12 text-center">
                            <House size={48} className="mx-auto mb-4 opacity-50" />
                            <p className="text-muted-foreground">Keine Immobilien gefunden</p>
                        </CardContent>
                    </Card>
                ) : (
                    <>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 stagger-children">
                            {properties.map((property) => (
                                <PropertyCard key={property.id} property={property} />
                            ))}
                        </div>
                        
                        {/* Pagination */}
                        {totalPages > 1 && (
                            <div className="flex items-center justify-center gap-2 mt-8" data-testid="public-pagination">
                                <Button
                                    variant="outline"
                                    size="icon"
                                    className="h-10 w-10 rounded-sm"
                                    onClick={() => setCurrentPage(p => p - 1)}
                                    disabled={currentPage === 1}
                                >
                                    <CaretLeft size={16} />
                                </Button>
                                <span className="text-sm text-muted-foreground px-4">
                                    Seite {currentPage} von {totalPages}
                                </span>
                                <Button
                                    variant="outline"
                                    size="icon"
                                    className="h-10 w-10 rounded-sm"
                                    onClick={() => setCurrentPage(p => p + 1)}
                                    disabled={currentPage === totalPages}
                                >
                                    <CaretRight size={16} />
                                </Button>
                            </div>
                        )}
                    </>
                )}
            </div>

            {/* Property Detail Modal */}
            {selectedProperty && (
                <PropertyDetail 
                    property={selectedProperty} 
                    onClose={() => setSelectedProperty(null)} 
                />
            )}
        </div>
    );
}
